import { useEffect, useRef } from 'react';
import { Dragon } from './Dragon';
import { spawnObstacle, spawnDiamond } from './Spawner';
import { isColliding } from './Collision';
import { ScreenShake } from './ScreenShake';
import { HitEffect } from './HitEffect';
import { loadBackground, loadObstacleSprites, loadDragonSprite } from './loadAssets';

const GAME_WIDTH = 800;
const GAME_HEIGHT = 450;

export default function GameCanvas({ difficulty, config, onScore, onDiamondCollected, onGameOver, onCollision }) {
  const canvasRef = useRef(null);
  const dragonRef = useRef(null);
  const obstaclesRef = useRef([]);
  const diamondsRef = useRef([]);
  const bgLayersRef = useRef([]);
  const obstacleSpritesRef = useRef(null);
  const shakeRef = useRef(new ScreenShake());
  const hitEffectsRef = useRef([]);
  const lastTimeRef = useRef(0);
  const lastObstacleRef = useRef(0);
  const lastDiamondRef = useRef(0);
  const runningRef = useRef(true);
  const assetsLoadedRef = useRef(false);
  
  // Store callbacks in refs to avoid dependency issues
  const onScoreRef = useRef(onScore);
  const onDiamondCollectedRef = useRef(onDiamondCollected);
  const onGameOverRef = useRef(onGameOver);
  const onCollisionRef = useRef(onCollision);
  
  // Update refs when callbacks change
  useEffect(() => {
    onScoreRef.current = onScore;
    onDiamondCollectedRef.current = onDiamondCollected;
    onGameOverRef.current = onGameOver;
    onCollisionRef.current = onCollision;
  }, [onScore, onDiamondCollected, onGameOver, onCollision]);

  // Load assets
  useEffect(() => {
    let mounted = true;

    Promise.all([
      loadBackground(),
      loadObstacleSprites(),
      loadDragonSprite()
    ]).then(([bgLayers, obstacleSprites, dragonSprite]) => {
      if (!mounted) return;
      
      bgLayersRef.current = bgLayers;
      obstacleSpritesRef.current = obstacleSprites;
      dragonRef.current = new Dragon(dragonSprite);
      assetsLoadedRef.current = true;
    }).catch((error) => {
      console.error('Error loading assets:', error);
      // Initialize with fallbacks even if assets fail
      if (mounted) {
        bgLayersRef.current = [];
        obstacleSpritesRef.current = {};
        dragonRef.current = new Dragon(null);
        assetsLoadedRef.current = true;
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  // Game loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = GAME_WIDTH;
    canvas.height = GAME_HEIGHT;

    // Initialize dragon if not already done
    if (!dragonRef.current) {
      dragonRef.current = new Dragon(null);
    }

    // Only reset if this is a new game start (not a re-render)
    const isNewGame = obstaclesRef.current.length === 0;
    
    if (isNewGame) {
      runningRef.current = true;
      lastTimeRef.current = performance.now();
      lastObstacleRef.current = lastTimeRef.current;
      lastDiamondRef.current = lastTimeRef.current;
      
      // Spawn first obstacle immediately to ensure visibility
      if (config && config.speed) {
        const firstObstacle = spawnObstacle(config, obstacleSpritesRef.current);
        if (firstObstacle) {
          firstObstacle.x = 700; // Start it visible on screen
          obstaclesRef.current.push(firstObstacle);
        }
      }
    } else {
      // Just ensure running state is set, don't reset obstacles
      runningRef.current = true;
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = performance.now();
        lastObstacleRef.current = lastTimeRef.current;
        lastDiamondRef.current = lastTimeRef.current;
      }
    }

    let animationId;

    const loop = (time) => {
      if (!runningRef.current) return;

      const delta = time - lastTimeRef.current;
      lastTimeRef.current = time;

      // Clear canvas
      ctx.clearRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

      // Draw base sky gradient first
      const skyGradient = ctx.createLinearGradient(0, 0, 0, GAME_HEIGHT);
      skyGradient.addColorStop(0, '#87CEEB');
      skyGradient.addColorStop(0.5, '#E0F6FF');
      skyGradient.addColorStop(1, '#98D8C8');
      ctx.fillStyle = skyGradient;
      ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

      // Apply screen shake
      const shake = shakeRef.current.update(delta);
      ctx.save();
      ctx.translate(shake.x, shake.y);

      // Draw background layers (parallax) - only if they have images
      bgLayersRef.current.forEach((layer, index) => {
        if (layer.image && layer.image.complete && layer.image.width > 0) {
          layer.update();
          layer.draw(ctx, GAME_WIDTH, GAME_HEIGHT);
        }
      });

      // Spawn obstacles
      if (config && config.obstacleRate) {
        const timeSinceLastObstacle = time - lastObstacleRef.current;
        if (timeSinceLastObstacle > config.obstacleRate) {
          const newObstacle = spawnObstacle(config, obstacleSpritesRef.current);
          if (newObstacle) {
            obstaclesRef.current.push(newObstacle);
            lastObstacleRef.current = time;
          }
        }
      }

      // Spawn diamonds
      if (time - lastDiamondRef.current > 2500) {
        diamondsRef.current.push(spawnDiamond());
        lastDiamondRef.current = time;
      }

      // Update and draw obstacles (before dragon so they appear behind)
      obstaclesRef.current.forEach((obstacle, index) => {
        if (!obstacle) return;
        
        obstacle.update();
        
        // Draw obstacle if any part of it is visible (even partially off-screen)
        if (obstacle.x < GAME_WIDTH && obstacle.x + obstacle.width > 0) {
          obstacle.draw(ctx);
        }

        // Check collision (only if obstacle is on screen)
        if (dragonRef.current && 
            obstacle.x < GAME_WIDTH && 
            obstacle.x + obstacle.width > 0 &&
            isColliding(dragonRef.current, obstacle)) {
          // Screen shake
          shakeRef.current.start(300, 8);

          // Hit effect
          hitEffectsRef.current.push(
            new HitEffect(
              dragonRef.current.x + dragonRef.current.width / 2,
              dragonRef.current.y + dragonRef.current.height / 2
            )
          );

          // Vibration (mobile)
          if (navigator.vibrate) {
            navigator.vibrate(80);
          }

          runningRef.current = false;
          // Use callbacks from refs
          if (onCollisionRef.current) onCollisionRef.current();
          if (onGameOverRef.current) onGameOverRef.current();
        }
      });

      // Remove off-screen obstacles (only when completely off the left side)
      obstaclesRef.current = obstaclesRef.current.filter(obstacle => {
        // Keep obstacle if any part is still visible or approaching from right
        return obstacle && obstacle.x + obstacle.width > -100;
      });

      // Update and draw diamonds
      diamondsRef.current = diamondsRef.current.filter((diamond, index) => {
        diamond.update(config.speed);
        diamond.draw(ctx);

        // Check collection
        if (dragonRef.current && isColliding(dragonRef.current, {
          x: diamond.x - diamond.size,
          y: diamond.y - diamond.size,
          width: diamond.size * 2,
          height: diamond.size * 2
        })) {
          // Use callbacks from refs
          if (onDiamondCollectedRef.current) onDiamondCollectedRef.current();
          if (onScoreRef.current) onScoreRef.current(50);
          return false; // Remove collected diamond
        }

        // Remove off-screen diamonds
        return diamond.x > -50;
      });

      // Update and draw dragon (on top of obstacles)
      if (dragonRef.current) {
        dragonRef.current.update();
        dragonRef.current.draw(ctx);
      }

      // Draw hit effects
      hitEffectsRef.current.forEach(effect => {
        effect.update();
        effect.draw(ctx);
      });
      hitEffectsRef.current = hitEffectsRef.current.filter(e => !e.isDone());

      // Red flash overlay on hit
      if (shakeRef.current.duration > 0) {
        ctx.fillStyle = 'rgba(255, 0, 0, 0.15)';
        ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
      }

      ctx.restore();

      if (runningRef.current) {
        animationId = requestAnimationFrame(loop);
      }
    };

    animationId = requestAnimationFrame(loop);

    return () => {
      runningRef.current = false;
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [config]); // Only depend on config, not callbacks

  // Touch and mouse controls
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const getCoords = (e) => {
      const rect = canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const scaleX = GAME_WIDTH / rect.width;
      const scaleY = GAME_HEIGHT / rect.height;
      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY
      };
    };

    const handleMove = (e) => {
      e.preventDefault();
      if (!dragonRef.current || !runningRef.current) return;
      const { x, y } = getCoords(e);
      // Center the dragon on the cursor/touch point
      dragonRef.current.move(
        x - dragonRef.current.width / 2,
        y - dragonRef.current.height / 2
      );
    };

    canvas.addEventListener('mousemove', handleMove);
    canvas.addEventListener('touchmove', handleMove, { passive: false });

    return () => {
      canvas.removeEventListener('mousemove', handleMove);
      canvas.removeEventListener('touchmove', handleMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: '100%',
        height: '100%',
        maxHeight: '500px',
        touchAction: 'none',
        display: 'block',
        backgroundColor: '#87CEEB'
      }}
    />
  );
}

